/**
 * The Recent-section conversation cap, and the arithmetic behind it.
 *
 * Lives outside both components because the cap has two independent trigger sites that must agree
 * about it: unpinning a conversation (ReyConversationList) and creating one at first send
 * (MessengerWindow.materializeDraftConversation). Pure functions, so that agreement is testable
 * without mounting either component — and so neither has to carry list arithmetic it would then
 * own a second copy of.
 */

/**
 * Recent only. The Pinned section is deliberately uncapped: pinning is how a user says "keep this
 * one", and a cap that evicted pinned conversations would make the pin meaningless.
 *
 * Mirrored server-side by MessengerApiFunctions.MaxUnpinnedReyConversations, which refuses a 31st
 * unpinned conversation the user creates outright. The two are independent enforcement points and have
 * to be changed together — the client number decides when the user is ASKED, the server number decides
 * what is actually allowed.
 *
 * A new morning brief is the one create nobody is asked about: the server gives up the same row
 * oldestUnpinnedRow picks (MessengerApiFunctions.PickReyConversationToEvict) to make room for it.
 */
export const RECENT_CONVERSATION_CAP = 30;

// Coerced to a real boolean because `isPinned` is ABSENT on rows served before pinning existed, and
// on any optimistic row built without it. ReyConversationList has its own isPinned() method for the
// same reason; this is deliberately a separate one-line copy rather than a shared dependency, so the
// component's method keeps its own contract.
const isPinnedRow = row => Boolean(row && row.isPinned);

// The Scheduled tasks thread. Coerced for the same reason isPinnedRow is: the flag is absent on rows
// served before it existed.
const isScheduledRow = row => Boolean(row && row.isScheduled);

/**
 * The rows the Recent section is made of.
 *
 * Takes the WHOLE conversation list, never the Recent section as rendered: recent_conversations is
 * derived from the search-filtered list, so counting that would let the cap be walked straight past
 * by typing in the search box.
 *
 * The Scheduled tasks thread is excluded alongside pinned rows, and the server's own count excludes
 * it too (MessengerApiFunctions.CountUnpinnedReyConversationsAsync). It is not something the user
 * created, so counting it would quietly cost every beta user one of their thirty — and because the
 * user never sends into it, it sorts by its creation date and sinks to the LEAST recently used row,
 * so oldestUnpinnedRow would then offer it as the conversation to give up, which is neither the
 * user's to delete nor of any use: the next run would simply make another one.
 */
export const unpinnedRows = rows =>
  (rows || []).filter(row => !isPinnedRow(row) && !isScheduledRow(row));

/**
 * The row the cap would evict: the LAST unpinned row in list order.
 *
 * The server returns conversations most-recently-used-first (by the user's own last message), so
 * the last one is the least recently used — "the last thread in the list" as the user reads it on
 * screen. Null when there is nothing
 * to evict, which callers must treat as "do not prompt" rather than "prompt about nothing".
 */
export const oldestUnpinnedRow = rows => {
  const unpinned = unpinnedRows(rows);
  return unpinned.length ? unpinned[unpinned.length - 1] : null;
};

/**
 * True when admitting `incoming` more unpinned conversations would leave Recent over the cap.
 *
 * `incoming` is 1 at both trigger sites (one unpin, one create) but is a parameter rather than a
 * baked-in constant so the comparison reads as arithmetic instead of an off-by-one to squint at.
 * At exactly the cap the answer is false: 30 is allowed, 31 is what prompts.
 */
export const wouldExceedRecentCap = (rows, incoming = 1) =>
  unpinnedRows(rows).length + incoming > RECENT_CONVERSATION_CAP;
